/**
 * Agent in-app notifications API.
 * Base path: /v1/agent/notifications (agent JWT only).
 */

import axiosInstance from "./Axios";

export type AgentNotification = {
  id: number;
  type: string;
  message: string | null;
  data: {
    process_agent_assignment_id?: number | null;
    process_id?: number | null;
    resume_id?: number | null;
    candidate_name?: string | null;
    job_title?: string | null;
  } | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
};

export type NotificationPage = {
  current_page: number;
  last_page: number;
  total: number;
  data: AgentNotification[];
};

const BASE = "/v1/agent/notifications";

// CV_UNAVAILABLE and CV_DECLINED are intentionally absent: the CV is gone for the agent.
const LINKABLE_TYPES = new Set(["CV_ASSIGNED", "CV_RELEASED", "CV_REOPENED"]);

export const isLinkable = (n: AgentNotification): boolean =>
  LINKABLE_TYPES.has(n.type) && Boolean(n.data?.process_agent_assignment_id);

export const apiErrorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.error || err?.response?.data?.message || fallback;

export const getUnreadCount = async (): Promise<number> => {
  const res = await axiosInstance.get(`${BASE}/unread-count`);
  return Number(res.data?.unread_count || 0);
};

export const getNotifications = async (
  page = 1,
  unreadOnly = false
): Promise<{ unreadCount: number; page: NotificationPage }> => {
  const res = await axiosInstance.get(BASE, {
    params: { page, ...(unreadOnly ? { unread: true } : {}) },
  });
  return {
    unreadCount: Number(res.data?.unread_count || 0),
    page: res.data?.result,
  };
};

export const markNotificationRead = async (
  id: number
): Promise<{ unreadCount: number; notification: AgentNotification }> => {
  const res = await axiosInstance.post(`${BASE}/${id}/read`);
  return {
    unreadCount: Number(res.data?.unread_count || 0),
    notification: res.data?.result,
  };
};

export const markAllNotificationsRead = async (): Promise<number> => {
  const res = await axiosInstance.post(`${BASE}/mark-all-read`);
  return Number(res.data?.unread_count || 0);
};
